import React, {PropsWithChildren, useEffect, useState} from 'react';
import {DebounceInput} from 'react-debounce-input';
import {getPlaceDetails, searchAddress} from '../../Foundations/Repositories/PlaceRepository';
import {IAddress} from '../../Foundations/Types/IAddress';
import Dropdown from '../../Components/Dropdown/Dropdown';
import {IPlace} from '../../Foundations/Types/IPlace';
import SvgIcon from '../../Components/Icons/SvgIcon';
import MapMarker from '../../Components/Icons/MapMarker';
import AirportIcon from '../../Components/Icons/AirportIcon';
import Suggestion from '../../Components/Suggestion/Suggestion';

interface IProps {
    onSelected(selected: IPlace | null): void;
    staticLocation: IPlace | null;
    initialLocation: IPlace | null;
}

const LocationInput: React.FC<IProps> = (props: PropsWithChildren<IProps>) => {
    const [result, setResult] = useState<IAddress[] | null>(null);
    const [query, setQuery] = useState(props.initialLocation !== null ? props.initialLocation.name : '');
    const [selectedIndex, setSelectedIndex] = useState<number>(-1);

    useEffect(() => {
        if (props.initialLocation !== null) {
            setQuery(props.initialLocation.name);
        }
    }, [props.initialLocation])

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        if (e.target.value.length < 3) {
            setResult(null);
            return;
        }

        const query: string = e.target.value;

       searchAddress(query).then((result: IAddress[]) => {
           setResult(result);
           setQuery(query);
       });
    }

    const handleSelection = (item: IAddress): void => {
       getPlaceDetails(item).then((result: IPlace | null) => {
           setQuery(item.description);
           setResult(null);
           props.onSelected(result);
       });
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
        if (result === null || result.length === 0) {
            setSelectedIndex(0);
            return;
        }

        if (event.key.toLowerCase() === 'arrowdown') {
            const newSelectedIndex: number = selectedIndex + 1 >= result.length - 1
                ? result.length - 1 : selectedIndex + 1;
            setSelectedIndex(newSelectedIndex);
        } else if (event.key.toLowerCase() === 'arrowup') {
            const newSelectedIndex: number = selectedIndex - 1 < 0
                ? 0 : selectedIndex - 1;
            setSelectedIndex(newSelectedIndex);
        } else if (event.key.toLowerCase() === 'enter' && typeof [selectedIndex] !== 'undefined') {
            handleSelection(result[selectedIndex]);
        }
    }

    const handleDropdownClose = () => {
        setResult(null);
        setSelectedIndex(-1);
    }

    return (
        <div className="relative bg-white shadow shadow-2lx flex p-2 rounded">
            <SvgIcon className="ml-0.5 fill-gray-500">
                <MapMarker />
            </SvgIcon>
            <DebounceInput
                disabled={props.staticLocation !== null}
                onChange={handleOnChange}
                className="pl-1.5 placeholder:text-gray-600 w-full outline-none truncate font-sans text-black-400 text-sm"
                onKeyDown={handleKeyDown}
                value={props.staticLocation !== null && query === '' ? props.staticLocation.name : query}
                placeholder="Search address"
            />
            {
                result !== null &&
                result.length > 0 &&
                <Dropdown className="mt-10 z-20" onClickOutside={handleDropdownClose}>
                    {
                        result.map((item: IAddress, index: number) =>
                            <div key={index}
                                 className={`m-2 text-sm p-2 text-black-400 rounded hover:bg-blue-200 cursor-pointer ${selectedIndex === index ? 'bg-blue-200' : ''}`}
                                 onClick={(): void => handleSelection(item)}
                            >
                                {
                                    item.types !== undefined && item.types.includes('airport') &&
                                    <span className="inline-block">
                                        <SvgIcon height="12" width="12" className="fill-gray-500 mr-2">
                                            <AirportIcon />
                                        </SvgIcon>
                                    </span>
                                }
                                <Suggestion 
                                    label={item.description} 
                                    searchTerm={query} 
                                />
                            </div>
                        )
                    }
                </Dropdown>
            }
        </div>
    )
}

export default LocationInput;
